import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Box,
  Chip,
  InputAdornment,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface EmployeeOption {
  empCode: number;
  empName: string;
  emailId?: string;
}

interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (employees: EmployeeOption[]) => Promise<void>;
  employees: EmployeeOption[];
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  open,
  onClose,
  onCreate,
  employees
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>([]);
  const [employeeError, setEmployeeError] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedEmployees([]);
      setEmployeeError("");
    }
  }, [open]);

  const handleDialogClose = (_event: object, reason: string) => {
    if (isSaving) {
      return;
    }

    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  const handleCreate = async () => {
    if (selectedEmployees.length === 0) {
      setEmployeeError("Employee name is required");
      return;
    }

    if (isSaving) {
      return;
    }

    setEmployeeError("");
    setIsSaving(true);
    try {
      await onCreate(selectedEmployees);
      setSelectedEmployees([]);
    } catch {
      // Parent handles error banner.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: '#fff',
            boxShadow: 24,
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#181f2a',
              boxShadow: '0 4px 32px 0 rgba(0,0,0,0.45)',
            },
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#0B3A6A',
            borderBottom: 'none',
            '@media (prefers-color-scheme: dark)': {
              color: '#fff',
              backgroundColor: '#181f2a',
              borderBottom: '1px solid #232c3a',
            },
          }}
        >
          Add Employee
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            sx={{ color: '#0B3A6A', '@media (prefers-color-scheme: dark)': { color: '#fff' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: '#fff',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#181f2a',
              borderBottom: '1px solid #232c3a',
            },
            borderBottom: 'none',
          }}
        >
          <Box sx={{ mt: 1 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                mb: 1,
                color: '#232c3a',
                '@media (prefers-color-scheme: dark)': {
                  color: '#fff',
                },
              }}
            >
              Employee Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              multiple
              options={employees}
              value={selectedEmployees}
              onChange={(_, newValue) => {
                setSelectedEmployees(newValue);
                if (newValue.length > 0) setEmployeeError("");
              }}
              getOptionLabel={(option) => `${option.empCode} - ${option.empName}`}
              isOptionEqualToValue={(option, value) => option.empCode === value.empCode}
              filterOptions={(options, { inputValue }) => {
                // Trim input, treat whitespace-only as empty string (show all options)
                const trimmed = inputValue.trim();
                if (!trimmed) return options;
                return options.filter(option =>
                  (`${option.empCode} - ${option.empName}`)
                    .toLowerCase()
                    .includes(trimmed.toLowerCase())
                );
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#fff',
                    color: '#232c3a',
                    // Light theme: make selected option more visible
                    '& .MuiAutocomplete-option[aria-selected="true"]': {
                      backgroundColor: '#e3edf7',
                      color: '#124b84',
                    },
                    // On hover, keep highlight strong
                    '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
                      backgroundColor: '#d0e3f7',
                      color: '#124b84',
                    },
                    '@media (prefers-color-scheme: dark)': {
                      backgroundColor: '#232c3a',
                      color: '#fff',
                      '& .MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: '#124b84',
                        color: '#fff',
                      },
                      '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
                        backgroundColor: '#124b84',
                        color: '#fff',
                      },
                    },
                  },
                },
                popper: {
                  sx: {
                    zIndex: 1302,
                  },
                },
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const { key: _keyProp, ...restProps } = tagProps as any;
                  return (
                    <Chip
                      key={option.empCode}
                      label={`${option.empCode} - ${option.empName}`}
                      {...restProps}
                      sx={{
                        backgroundColor: '#e3edf7',
                        color: '#124b84',
                        fontWeight: 500,
                        borderRadius: '16px',
                        '@media (prefers-color-scheme: dark)': {
                          backgroundColor: '#232c3a',
                          color: '#fff',
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search employee"
                  error={!!employeeError}
                  helperText={employeeError}
                  sx={{
                    backgroundColor: '#232c3a',
                    borderRadius: 2,
                    color: '#fff',
                    '& .MuiInputBase-input': {
                      color: '#fff',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.12)',
                    },
                    '@media (prefers-color-scheme: light)': {
                      backgroundColor: '#fff',
                      color: '#232c3a',
                      '& .MuiInputBase-input': {
                        color: '#232c3a',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d0d5dd',
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: params.inputProps.value ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="clear employee search"
                          onClick={() => {
                            if (params.inputProps && params.inputProps.onChange && params.inputProps.ref) {
                              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
                              const ref = params.inputProps.ref;
                              // Handle both RefObject and function ref
                              let inputEl: HTMLInputElement | null = null;
                              if (typeof ref === 'function') {
                                // Not supported for clearing, skip
                              } else if ('current' in ref) {
                                inputEl = ref.current;
                              }
                              if (inputEl) {
                                nativeInputValueSetter?.call(inputEl, "");
                                const event = new Event('input', { bubbles: true });
                                inputEl.dispatchEvent(event);
                              }
                            }
                          }}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2, backgroundColor: 'transparent', '@media (prefers-color-scheme: dark)': { backgroundColor: '#181f2a' } }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={isSaving}
            sx={{
              backgroundColor: '#124B84',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': { backgroundColor: '#0f3f70' },
              '&:disabled': { backgroundColor: '#bdbdbd' },
              '@media (prefers-color-scheme: dark)': {
                backgroundColor: '#223e6a',
                color: '#fff',
                '&:hover': { backgroundColor: '#124B84' },
                '&:disabled': { backgroundColor: '#232c3a' },
              },
            }}
          >
            {isSaving ? 'Creating...' : 'Create'}
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isSaving}
            sx={{
              backgroundColor: '#e53935',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '@media (prefers-color-scheme: dark)': {
                backgroundColor: '#d32f2f',
                color: '#fff',
                '&:hover': { backgroundColor: '#b71c1c' },
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddEmployeeModal;